import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { EditSaveCancel } from '@/components/ui/edit-save-cancel';
import { Checkbox } from '@/components/ui/checkbox';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import { formatPhoneNumber, type UserProfileResponse } from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';

/**
 * Type guard to check if a string is a valid Status
 */
function isStatus(value: string): value is Status {
    return Object.values(Status).some((status) => status === value);
}

export interface EmailUpdatePayload {
    emails: string[];
    primary_email: string;
}

export interface EmailUpdatePayloadInput {
    user: BasicInformationUser;
    editingEmails: string[];
    editingPrimaryEmail: string;
}

export interface PhoneUpdatePayload {
    phones: string[];
    primary_phone: string | null;
}

export interface PhoneUpdatePayloadInput {
    user: BasicInformationUser;
    editingPhones: string[];
    editingPrimaryPhone: string | null;
}

function areListsEqual(originalValues: string[], updatedValues: string[]): boolean {
    if (originalValues.length !== updatedValues.length) {
        return false;
    }

    const updatedValueSet = new Set<string>(updatedValues);
    for (const value of originalValues) {
        if (!updatedValueSet.has(value)) {
            return false;
        }
    }

    return true;
}

function getUserPhones(user: BasicInformationUser): string[] {
    return user.phones ?? [];
}

function getUserPrimaryPhone(user: BasicInformationUser): string | null {
    return user.primary_phone ?? null;
}

function normalizePhoneInput(value: string): string {
    return value.trim().replace(/[\s()-]+/g, '');
}

function isPhoneInputValid(value: string): boolean {
    const normalizedValue = normalizePhoneInput(value);
    return normalizedValue.length > 0 && /^(\+\d+|\d+)$/.test(normalizedValue);
}

function formatPhoneForDisplay(value: string): string {
    return formatPhoneNumber(value) || value;
}

export type BasicInformationUser = Omit<UserProfileResponse, 'user_id'> & {
    user_id?: number;
};

export function buildEmailUpdatePayload(input: EmailUpdatePayloadInput): EmailUpdatePayload | null {
    const { user, editingEmails, editingPrimaryEmail } = input;
    const emailListChanged = !areListsEqual(user.emails, editingEmails);
    const primaryChanged = editingPrimaryEmail !== user.primary_email;

    if (!emailListChanged && !primaryChanged) {
        return null;
    }

    const uniqueEmails = new Set<string>(editingEmails);
    if (uniqueEmails.size !== editingEmails.length) {
        throw new Error('Emails must be unique');
    }
    if (!uniqueEmails.has(editingPrimaryEmail)) {
        throw new Error('Primary email must be included in emails');
    }

    return {
        emails: editingEmails,
        primary_email: editingPrimaryEmail,
    };
}

export function buildPhoneUpdatePayload(input: PhoneUpdatePayloadInput): PhoneUpdatePayload | null {
    const { user, editingPrimaryPhone } = input;
    const editingPhones = input.editingPhones.map((phone) => normalizePhoneInput(phone));
    const currentPhones = getUserPhones(user);
    const currentPrimaryPhone = getUserPrimaryPhone(user);
    const phoneListChanged = !areListsEqual(currentPhones, editingPhones);
    const primaryChanged = editingPrimaryPhone !== currentPrimaryPhone;

    if (!phoneListChanged && !primaryChanged) {
        return null;
    }

    const uniquePhones = new Set<string>(editingPhones);
    if (uniquePhones.size !== editingPhones.length) {
        throw new Error('Phone numbers must be unique');
    }
    if (editingPrimaryPhone !== null && !uniquePhones.has(editingPrimaryPhone)) {
        throw new Error('Primary phone number must be included in phone numbers');
    }
    if (editingPrimaryPhone === null && editingPhones.length > 0) {
        throw new Error('Primary phone number is required when phone numbers are present');
    }

    return {
        phones: editingPhones,
        primary_phone: editingPrimaryPhone,
    };
}

interface BasicInformationCardProps {
    user: BasicInformationUser;
    isAdmin: boolean;
    onSave: (data: {
        first_name?: string | null;
        last_name?: string | null;
        primary_email?: string;
        status?: Status;
        emails?: {
            emails: string[];
            primary_email: string;
        };
        phones?: {
            phones: string[];
            primary_phone: string | null;
        };
    }) => void;
    onRequestAddEmailVerification: ((request: {
        readonly email: string;
        readonly skip_verification?: boolean;
    }) => Promise<void>) | null;
    onRequestAddPhoneVerification: ((request: {
        readonly phone: string;
        readonly skip_verification?: boolean;
    }) => Promise<void>) | null;
    onVerifyAddPhone: ((request: { readonly phone: string; readonly code: string }) => Promise<void>) | null;
    isSuperadmin: boolean;
    addEmailRequestMessage: string | null;
    addEmailRequestError: Error | null;
    isRequestingAddEmail: boolean;
    addPhoneRequestMessage: string | null;
    addPhoneRequestError: Error | null;
    isRequestingAddPhone: boolean;
    verifyAddPhoneError: Error | null;
    isVerifyingAddPhone: boolean;
    isLoading: boolean;
    error: Error | null;
}

export function BasicInformationCard({
    user,
    isAdmin,
    onSave,
    onRequestAddEmailVerification,
    onRequestAddPhoneVerification,
    onVerifyAddPhone,
    isSuperadmin,
    addEmailRequestMessage,
    addEmailRequestError,
    isRequestingAddEmail,
    addPhoneRequestMessage,
    addPhoneRequestError,
    isRequestingAddPhone,
    verifyAddPhoneError,
    isVerifyingAddPhone,
    isLoading,
    error,
}: BasicInformationCardProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [status, setStatus] = useState<Status>('ACTIVE');
    const [hasInitiatedSave, setHasInitiatedSave] = useState(false);

    // Email editing state
    const [editingEmails, setEditingEmails] = useState<string[]>([]);
    const [editingPrimaryEmail, setEditingPrimaryEmail] = useState('');
    const [editingEmailIndex, setEditingEmailIndex] = useState<number | null>(null);
    const [editingEmailValue, setEditingEmailValue] = useState('');
    const [showAddEmailDialog, setShowAddEmailDialog] = useState(false);
    const [newEmailValue, setNewEmailValue] = useState('');
    const [skipVerificationForNewEmail, setSkipVerificationForNewEmail] = useState(false);
    // Track original email positions to detect edits
    const [originalEmailMap, setOriginalEmailMap] = useState<Map<string, string>>(new Map());

    // Phone editing state
    const [editingPhones, setEditingPhones] = useState<string[]>([]);
    const [editingPrimaryPhone, setEditingPrimaryPhone] = useState<string | null>(null);
    const [editingPhoneIndex, setEditingPhoneIndex] = useState<number | null>(null);
    const [editingPhoneValue, setEditingPhoneValue] = useState('');
    const [showAddPhoneDialog, setShowAddPhoneDialog] = useState(false);
    const [newPhoneValue, setNewPhoneValue] = useState('');
    const [skipVerificationForNewPhone, setSkipVerificationForNewPhone] = useState(false);
    const [pendingPhoneVerification, setPendingPhoneVerification] = useState<string | null>(null);
    const [phoneVerificationCode, setPhoneVerificationCode] = useState('');

    // Initialize state when user data changes
    useEffect(() => {
        if (user) {
            setFirstName(user.first_name ?? '');
            setLastName(user.last_name ?? '');
            if (isStatus(user.status)) {
                setStatus(user.status);
            } else {
                throw new Error(`Invalid user status: ${user.status}`);
            }
            setEditingEmails([...user.emails]);
            setEditingPrimaryEmail(user.primary_email);
            setEditingPhones([...getUserPhones(user)]);
            setEditingPrimaryPhone(getUserPrimaryPhone(user));
            // Initialize original email map (email -> itself)
            const map = new Map<string, string>();
            user.emails.forEach((email) => map.set(email, email));
            setOriginalEmailMap(map);
        }
    }, [user]);

    // Exit edit mode when save is successful
    useEffect(() => {
        if (hasInitiatedSave && !isLoading && !error && isEditing) {
            // Save completed successfully, exit edit mode
            setIsEditing(false);
            setHasInitiatedSave(false);
        }
    }, [hasInitiatedSave, isLoading, error, isEditing]);

    // Reset save flag if there's an error
    useEffect(() => {
        if (error) {
            setHasInitiatedSave(false);
        }
    }, [error]);

    useUnsavedChangesRegistration(isEditing);

    const handleEdit = () => {
        setFirstName(user.first_name ?? '');
        setLastName(user.last_name ?? '');
        if (isStatus(user.status)) {
            setStatus(user.status);
        } else {
            throw new Error(`Invalid user status: ${user.status}`);
        }
        setEditingEmails([...user.emails]);
        setEditingPrimaryEmail(user.primary_email);
        setEditingPhones([...getUserPhones(user)]);
        setEditingPrimaryPhone(getUserPrimaryPhone(user));
        setEditingEmailIndex(null);
        setEditingEmailValue('');
        setEditingPhoneIndex(null);
        setEditingPhoneValue('');
        setNewEmailValue('');
        setNewPhoneValue('');
        setSkipVerificationForNewEmail(false);
        setSkipVerificationForNewPhone(false);
        setPendingPhoneVerification(null);
        setPhoneVerificationCode('');
        setShowAddEmailDialog(false);
        setShowAddPhoneDialog(false);
        // Reset original email map
        const map = new Map<string, string>();
        user.emails.forEach((email) => map.set(email, email));
        setOriginalEmailMap(map);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setFirstName(user.first_name ?? '');
        setLastName(user.last_name ?? '');
        if (isStatus(user.status)) {
            setStatus(user.status);
        } else {
            throw new Error(`Invalid user status: ${user.status}`);
        }
        setEditingEmails([...user.emails]);
        setEditingPrimaryEmail(user.primary_email);
        setEditingPhones([...getUserPhones(user)]);
        setEditingPrimaryPhone(getUserPrimaryPhone(user));
        setEditingEmailIndex(null);
        setEditingEmailValue('');
        setEditingPhoneIndex(null);
        setEditingPhoneValue('');
        setNewEmailValue('');
        setNewPhoneValue('');
        setSkipVerificationForNewEmail(false);
        setSkipVerificationForNewPhone(false);
        setPendingPhoneVerification(null);
        setPhoneVerificationCode('');
        setShowAddEmailDialog(false);
        setShowAddPhoneDialog(false);
        // Reset original email map
        const map = new Map<string, string>();
        user.emails.forEach((email) => map.set(email, email));
        setOriginalEmailMap(map);
        setIsEditing(false);
        setHasInitiatedSave(false);
    };

    const handleSave = () => {
        const emailUpdatePayload = buildEmailUpdatePayload({
            user,
            editingEmails,
            editingPrimaryEmail,
        });
        const phoneUpdatePayload = buildPhoneUpdatePayload({
            user,
            editingPhones,
            editingPrimaryPhone,
        });
        const updates: {
            first_name?: string | null;
            last_name?: string | null;
            primary_email?: string;
            status?: Status;
            emails?: {
                emails: string[];
                primary_email: string;
            };
            phones?: {
                phones: string[];
                primary_phone: string | null;
            };
        } = {};

        if (firstName !== (user.first_name ?? '')) {
            updates.first_name = firstName || null;
        }
        if (lastName !== (user.last_name ?? '')) {
            updates.last_name = lastName || null;
        }
        if (status !== user.status) {
            updates.status = status;
        }

        if (emailUpdatePayload) {
            updates.emails = emailUpdatePayload;
        }
        if (phoneUpdatePayload) {
            updates.phones = phoneUpdatePayload;
        }

        if (Object.keys(updates).length > 0) {
            setHasInitiatedSave(true);
            onSave(updates);
        } else {
            setIsEditing(false);
        }
    };

    const handleEmailFocus = (index: number) => {
        if (!isEditing) return;
        setEditingEmailIndex(index);
        const emailValue = editingEmails[index];
        if (emailValue) {
            setEditingEmailValue(emailValue);
        }
    };

    const handleEmailBlur = (index: number) => {
        if (editingEmailIndex !== index) return;

        const originalEmail = editingEmails[index];
        if (!originalEmail) return;

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (editingEmailValue && !emailRegex.test(editingEmailValue)) {
            // Invalid email, revert to original
            setEditingEmailValue(originalEmail);
            setEditingEmailIndex(null);
            setEditingEmailValue('');
            return;
        }

        if (editingEmailValue && editingEmailValue !== originalEmail) {
            // Check if new email already exists
            if (editingEmails.includes(editingEmailValue) && editingEmailValue !== originalEmail) {
                // Email already exists, revert
                setEditingEmailValue(originalEmail);
                setEditingEmailIndex(null);
                setEditingEmailValue('');
                return;
            }

            // Update the email in the array
            const newEmails = [...editingEmails];
            newEmails[index] = editingEmailValue;
            setEditingEmails(newEmails);

            // Update the original email map
            const newMap = new Map(originalEmailMap);
            const mappedOriginal = originalEmailMap.get(originalEmail) || originalEmail;
            newMap.delete(originalEmail);
            newMap.set(editingEmailValue, mappedOriginal);
            setOriginalEmailMap(newMap);

            // If this was the primary email, update primary too
            if (originalEmail === editingPrimaryEmail) {
                setEditingPrimaryEmail(editingEmailValue);
            }
        }

        setEditingEmailIndex(null);
        setEditingEmailValue('');
    };

    const handleEmailKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleEmailBlur(index);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingEmailIndex(null);
            const emailValue = editingEmails[index];
            if (emailValue) {
                setEditingEmailValue(emailValue);
            }
        }
    };

    const handleMakePrimary = (email: string) => {
        if (!isEditing) return;
        setEditingPrimaryEmail(email);
    };

    const handlePhoneFocus = (index: number) => {
        if (!isEditing) return;
        setEditingPhoneIndex(index);
        const phoneValue = editingPhones[index];
        if (phoneValue) {
            setEditingPhoneValue(formatPhoneForDisplay(phoneValue));
        }
    };

    const handlePhoneBlur = (index: number) => {
        if (editingPhoneIndex !== index) return;

        const originalPhone = editingPhones[index];
        if (!originalPhone) return;

        if (editingPhoneValue && !isPhoneInputValid(editingPhoneValue)) {
            setEditingPhoneValue(originalPhone);
            setEditingPhoneIndex(null);
            setEditingPhoneValue('');
            return;
        }

        const normalizedPhone = normalizePhoneInput(editingPhoneValue);
        if (normalizedPhone && normalizedPhone !== originalPhone) {
            if (editingPhones.includes(normalizedPhone)) {
                setEditingPhoneValue(originalPhone);
                setEditingPhoneIndex(null);
                setEditingPhoneValue('');
                return;
            }

            const newPhones = [...editingPhones];
            newPhones[index] = normalizedPhone;
            setEditingPhones(newPhones);

            if (originalPhone === editingPrimaryPhone) {
                setEditingPrimaryPhone(normalizedPhone);
            }
        }

        setEditingPhoneIndex(null);
        setEditingPhoneValue('');
    };

    const handlePhoneKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handlePhoneBlur(index);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingPhoneIndex(null);
            const phoneValue = editingPhones[index];
            if (phoneValue) {
                setEditingPhoneValue(formatPhoneForDisplay(phoneValue));
            }
        }
    };

    const handleMakePrimaryPhone = (phone: string) => {
        if (!isEditing) return;
        setEditingPrimaryPhone(phone);
    };

    const handleAddEmail = async () => {
        if (!newEmailValue.trim()) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmailValue.trim())) {
            return; // Invalid email
        }

        const trimmedEmail = newEmailValue.trim();
        if (editingEmails.includes(trimmedEmail)) {
            return; // Email already exists
        }

        if (onRequestAddEmailVerification !== null) {
            await onRequestAddEmailVerification({
                email: trimmedEmail,
                skip_verification: isSuperadmin ? skipVerificationForNewEmail : undefined,
            });
            setNewEmailValue('');
            setSkipVerificationForNewEmail(false);
            setShowAddEmailDialog(false);
            return;
        }

        setEditingEmails([...editingEmails, trimmedEmail]);
        setNewEmailValue('');
        setSkipVerificationForNewEmail(false);
        setShowAddEmailDialog(false);
    };

    const handleOpenAddEmailDialog = () => {
        setNewEmailValue('');
        setSkipVerificationForNewEmail(false);
        setShowAddEmailDialog(true);
    };

    const handleAddPhone = async () => {
        if (!newPhoneValue.trim() || !isPhoneInputValid(newPhoneValue)) return;

        const normalizedPhone = normalizePhoneInput(newPhoneValue);
        if (editingPhones.includes(normalizedPhone)) {
            return;
        }

        if (onRequestAddPhoneVerification !== null) {
            await onRequestAddPhoneVerification({
                phone: normalizedPhone,
                skip_verification: isSuperadmin ? skipVerificationForNewPhone : undefined,
            });
            if (isSuperadmin && skipVerificationForNewPhone) {
                setNewPhoneValue('');
                setSkipVerificationForNewPhone(false);
                setShowAddPhoneDialog(false);
                return;
            }
            setPendingPhoneVerification(normalizedPhone);
            return;
        }

        setEditingPhones([...editingPhones, normalizedPhone]);
        if (editingPrimaryPhone === null) {
            setEditingPrimaryPhone(normalizedPhone);
        }
        setNewPhoneValue('');
        setShowAddPhoneDialog(false);
    };

    const handleOpenAddPhoneDialog = () => {
        setNewPhoneValue('');
        setSkipVerificationForNewPhone(false);
        setPendingPhoneVerification(null);
        setPhoneVerificationCode('');
        setShowAddPhoneDialog(true);
    };

    const handleVerifyAddPhone = async () => {
        if (
            pendingPhoneVerification === null ||
            phoneVerificationCode.trim().length === 0 ||
            onVerifyAddPhone === null
        ) {
            return;
        }
        await onVerifyAddPhone({
            phone: pendingPhoneVerification,
            code: phoneVerificationCode.trim(),
        });
        setNewPhoneValue('');
        setSkipVerificationForNewPhone(false);
        setPendingPhoneVerification(null);
        setPhoneVerificationCode('');
        setShowAddPhoneDialog(false);
    };

    const handleRemoveEmail = (email: string) => {
        if (!isEditing) return;

        // Can't remove primary email if it's the only one
        if (email === editingPrimaryEmail && editingEmails.length === 1) {
            return;
        }

        // If removing primary, set first other email as primary
        if (email === editingPrimaryEmail && editingEmails.length > 1) {
            const otherEmail = editingEmails.find((e) => e !== email);
            if (otherEmail) {
                setEditingPrimaryEmail(otherEmail);
            }
        }

        setEditingEmails(editingEmails.filter((e) => e !== email));
        // Remove from original email map
        const newMap = new Map(originalEmailMap);
        newMap.delete(email);
        setOriginalEmailMap(newMap);
    };

    const handleRemovePhone = (phone: string) => {
        if (!isEditing) return;

        if (phone === editingPrimaryPhone && editingPhones.length === 1) {
            setEditingPrimaryPhone(null);
            setEditingPhones([]);
            return;
        }

        if (phone === editingPrimaryPhone && editingPhones.length > 1) {
            const otherPhone = editingPhones.find((candidate) => candidate !== phone);
            setEditingPrimaryPhone(otherPhone ?? null);
        }

        setEditingPhones(editingPhones.filter((candidate) => candidate !== phone));
    };

    const handleNewEmailKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            triggerAddEmailRequest();
        }
    };

    const handleNewPhoneKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (pendingPhoneVerification === null) {
                triggerAddPhoneRequest();
            } else {
                triggerVerifyAddPhoneRequest();
            }
        }
    };

    const triggerAddEmailRequest = (): void => {
        handleAddEmail().then(
            () => undefined,
            () => undefined
        );
    };

    const triggerAddPhoneRequest = (): void => {
        handleAddPhone().then(
            () => undefined,
            () => undefined
        );
    };

    const triggerVerifyAddPhoneRequest = (): void => {
        handleVerifyAddPhone().then(
            () => undefined,
            () => undefined
        );
    };

    const isNewEmailValid = () => {
        if (!newEmailValue.trim()) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmailValue.trim())) return false;
        if (editingEmails.includes(newEmailValue.trim())) return false;
        return true;
    };

    const isNewPhoneValid = () => {
        if (!newPhoneValue.trim()) return false;
        const normalizedPhone = normalizePhoneInput(newPhoneValue);
        if (!isPhoneInputValid(newPhoneValue)) return false;
        if (editingPhones.includes(normalizedPhone)) return false;
        return true;
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Basic Information</CardTitle>
                {isAdmin && (
                    <EditSaveCancel
                        onEdit={handleEdit}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        isLoading={isLoading}
                    />
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    {isEditing ? (
                        <Input
                            id="firstName"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Enter first name"
                        />
                    ) : (
                        <p className="text-sm">{user.first_name || '—'}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    {isEditing ? (
                        <Input
                            id="lastName"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Enter last name"
                        />
                    ) : (
                        <p className="text-sm">{user.last_name || '—'}</p>
                    )}
                </div>

                {/* All Emails Section */}
                <div className="space-y-2">
                    <Label>All Emails</Label>
                    <div className="space-y-2">
                        {isEditing ? (
                            <>
                                {editingEmails.map((email, idx) => {
                                    const isPrimary = email === editingPrimaryEmail;
                                    const isEditingThis = editingEmailIndex === idx;

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-2 border rounded-md gap-2"
                                        >
                                            <div className="flex items-center gap-2 flex-1">
                                                {isEditingThis ? (
                                                    <Input
                                                        type="email"
                                                        value={editingEmailValue}
                                                        onChange={(e) =>
                                                            setEditingEmailValue(e.target.value)
                                                        }
                                                        onBlur={() => handleEmailBlur(idx)}
                                                        onKeyDown={(e) =>
                                                            handleEmailKeyDown(e, idx)
                                                        }
                                                        className="flex-1"
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <>
                                                        <div
                                                            className="flex-1 cursor-text"
                                                            onClick={() => handleEmailFocus(idx)}
                                                        >
                                                            <Badge
                                                                variant={
                                                                    isPrimary
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                className="text-sm"
                                                            >
                                                                {email}
                                                            </Badge>
                                                        </div>
                                                        {isPrimary ? (
                                                            <Badge variant="secondary">
                                                                Primary
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="secondary"
                                                                className="opacity-60 cursor-pointer hover:opacity-100"
                                                                onClick={() =>
                                                                    handleMakePrimary(email)
                                                                }
                                                            >
                                                                Make primary
                                                            </Badge>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {!isEditingThis && (
                                                <div className="flex gap-2">
                                                    {!isPrimary && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-red-600 hover:text-red-700"
                                                            onClick={() => handleRemoveEmail(email)}
                                                        >
                                                            −
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Add email button */}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleOpenAddEmailDialog}
                                    className="w-full"
                                >
                                    +
                                </Button>
                            </>
                        ) : (
                            <>
                                {user.emails.map((email, idx) => {
                                    const isPrimary = email === user.primary_email;

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-2 border rounded-md"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Badge variant={isPrimary ? 'default' : 'outline'}>
                                                    {email}
                                                </Badge>
                                                {isPrimary && (
                                                    <Badge variant="secondary">Primary</Badge>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>

                {/* All Phone Numbers Section */}
                <div className="space-y-2">
                    <Label>All Phone Numbers</Label>
                    <div className="space-y-2">
                        {isEditing ? (
                            <>
                                {editingPhones.map((phone, idx) => {
                                    const isPrimary = phone === editingPrimaryPhone;
                                    const isEditingThis = editingPhoneIndex === idx;

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-2 border rounded-md gap-2"
                                        >
                                            <div className="flex items-center gap-2 flex-1">
                                                {isEditingThis ? (
                                                    <Input
                                                        type="tel"
                                                        value={editingPhoneValue}
                                                        onChange={(e) =>
                                                            setEditingPhoneValue(e.target.value)
                                                        }
                                                        onBlur={() => handlePhoneBlur(idx)}
                                                        onKeyDown={(e) =>
                                                            handlePhoneKeyDown(e, idx)
                                                        }
                                                        className="flex-1"
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <>
                                                        <div
                                                            className="flex-1 cursor-text"
                                                            onClick={() => handlePhoneFocus(idx)}
                                                        >
                                                            <Badge
                                                                variant={
                                                                    isPrimary
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                className="text-sm"
                                                            >
                                                                {formatPhoneForDisplay(phone)}
                                                            </Badge>
                                                        </div>
                                                        {isPrimary ? (
                                                            <Badge variant="secondary">
                                                                Primary
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="secondary"
                                                                className="opacity-60 cursor-pointer hover:opacity-100"
                                                                onClick={() =>
                                                                    handleMakePrimaryPhone(phone)
                                                                }
                                                            >
                                                                Make primary
                                                            </Badge>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {!isEditingThis && (
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-red-600 hover:text-red-700"
                                                        onClick={() => handleRemovePhone(phone)}
                                                    >
                                                        -
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleOpenAddPhoneDialog}
                                    className="w-full"
                                >
                                    +
                                </Button>
                            </>
                        ) : getUserPhones(user).length > 0 ? (
                            <>
                                {getUserPhones(user).map((phone, idx) => {
                                    const isPrimary = phone === getUserPrimaryPhone(user);

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-2 border rounded-md"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Badge variant={isPrimary ? 'default' : 'outline'}>
                                                    {formatPhoneForDisplay(phone)}
                                                </Badge>
                                                {isPrimary && (
                                                    <Badge variant="secondary">Primary</Badge>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        ) : (
                            <p className="text-sm text-muted-foreground">No phone numbers</p>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    {isEditing ? (
                        <Select
                            value={status}
                            onValueChange={(v) => {
                                if (isStatus(v)) {
                                    setStatus(v);
                                } else {
                                    throw new Error(`Invalid status selected: ${v}`);
                                }
                            }}
                        >
                            <SelectTrigger id="status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ACTIVE">Active</SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="DISABLED">Disabled</SelectItem>
                                <SelectItem value="DELETED">Deleted</SelectItem>
                            </SelectContent>
                        </Select>
                    ) : (
                        <Badge
                            variant={
                                user.status === 'ACTIVE'
                                    ? 'default'
                                    : user.status === 'PENDING'
                                      ? 'secondary'
                                      : 'destructive'
                            }
                        >
                            {user.status}
                        </Badge>
                    )}
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {error instanceof Error ? error.message : 'Failed to update user'}
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>

            {/* Add Email Dialog */}
            <Dialog open={showAddEmailDialog} onOpenChange={setShowAddEmailDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Email Address</DialogTitle>
                        <DialogDescription>
                            Enter a new email address to add to this user account.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="newEmail">Email Address</Label>
                            <Input
                                id="newEmail"
                                type="email"
                                placeholder="user@example.com"
                                value={newEmailValue}
                                onChange={(e) => setNewEmailValue(e.target.value)}
                                onKeyDown={handleNewEmailKeyDown}
                                autoFocus
                                disabled={isRequestingAddEmail}
                            />
                            {newEmailValue.trim() && !isNewEmailValid() && (
                                <p className="text-xs text-destructive">
                                    {editingEmails.includes(newEmailValue.trim())
                                        ? 'This email is already in the list'
                                        : 'Please enter a valid email address'}
                                </p>
                            )}
                        </div>
                        {isSuperadmin && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skipVerificationForBasicInfoNewEmail"
                                    checked={skipVerificationForNewEmail}
                                    onCheckedChange={(checked) =>
                                        setSkipVerificationForNewEmail(checked === true)
                                    }
                                    disabled={isRequestingAddEmail}
                                />
                                <Label htmlFor="skipVerificationForBasicInfoNewEmail">
                                    Skip verification (superadmin)
                                </Label>
                            </div>
                        )}
                        {addEmailRequestError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {addEmailRequestError instanceof Error
                                        ? addEmailRequestError.message
                                        : 'Failed to send verification email'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {addEmailRequestMessage !== null && (
                            <Alert>
                                <AlertDescription>{addEmailRequestMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddEmailDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={triggerAddEmailRequest}
                            disabled={!isNewEmailValid() || isRequestingAddEmail}
                        >
                            {isRequestingAddEmail
                                ? skipVerificationForNewEmail
                                    ? 'Adding...'
                                    : 'Sending...'
                                : skipVerificationForNewEmail
                                  ? 'Add Email'
                                  : 'Send Verification Email'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Phone Dialog */}
            <Dialog open={showAddPhoneDialog} onOpenChange={setShowAddPhoneDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Phone Number</DialogTitle>
                        <DialogDescription>
                            Enter a new phone number to add to this user account.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="newPhone">Phone Number</Label>
                            <Input
                                id="newPhone"
                                type="tel"
                                placeholder="(555) 123-4567"
                                value={newPhoneValue}
                                onChange={(e) => setNewPhoneValue(formatPhoneNumber(e.target.value))}
                                onKeyDown={handleNewPhoneKeyDown}
                                autoFocus
                                disabled={
                                    pendingPhoneVerification !== null ||
                                    isRequestingAddPhone ||
                                    isVerifyingAddPhone
                                }
                            />
                            {newPhoneValue.trim() && !isNewPhoneValid() && (
                                <p className="text-xs text-destructive">
                                    {editingPhones.includes(normalizePhoneInput(newPhoneValue))
                                        ? 'This phone number is already in the list'
                                        : 'Please enter a valid phone number'}
                                </p>
                            )}
                        </div>
                        {isSuperadmin &&
                            onRequestAddPhoneVerification !== null &&
                            pendingPhoneVerification === null && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skipVerificationForBasicInfoNewPhone"
                                    checked={skipVerificationForNewPhone}
                                    onCheckedChange={(checked) =>
                                        setSkipVerificationForNewPhone(checked === true)
                                    }
                                    disabled={isRequestingAddPhone}
                                />
                                <Label htmlFor="skipVerificationForBasicInfoNewPhone">
                                    Skip verification (superadmin)
                                </Label>
                            </div>
                        )}
                        {pendingPhoneVerification !== null && (
                            <div className="space-y-2">
                                <Label htmlFor="phoneVerificationCode">Verification Code</Label>
                                <Input
                                    id="phoneVerificationCode"
                                    inputMode="numeric"
                                    value={phoneVerificationCode}
                                    onChange={(e) => setPhoneVerificationCode(e.target.value)}
                                    onKeyDown={handleNewPhoneKeyDown}
                                    disabled={isVerifyingAddPhone}
                                />
                            </div>
                        )}
                        {addPhoneRequestError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {addPhoneRequestError instanceof Error
                                        ? addPhoneRequestError.message
                                        : 'Failed to send phone verification code'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {verifyAddPhoneError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {verifyAddPhoneError instanceof Error
                                        ? verifyAddPhoneError.message
                                        : 'Failed to verify phone number'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {addPhoneRequestMessage !== null && (
                            <Alert>
                                <AlertDescription>{addPhoneRequestMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAddPhoneDialog(false);
                                setNewPhoneValue('');
                                setSkipVerificationForNewPhone(false);
                                setPendingPhoneVerification(null);
                                setPhoneVerificationCode('');
                            }}
                        >
                            Cancel
                        </Button>
                        {pendingPhoneVerification === null ? (
                            <Button
                                onClick={triggerAddPhoneRequest}
                                disabled={!isNewPhoneValid() || isRequestingAddPhone}
                            >
                                {isRequestingAddPhone
                                    ? skipVerificationForNewPhone
                                        ? 'Adding...'
                                        : 'Sending...'
                                    : onRequestAddPhoneVerification === null ||
                                        skipVerificationForNewPhone
                                      ? 'Add Phone Number'
                                      : 'Send Verification Code'}
                            </Button>
                        ) : (
                            <Button
                                onClick={triggerVerifyAddPhoneRequest}
                                disabled={
                                    phoneVerificationCode.trim().length === 0 ||
                                    isVerifyingAddPhone
                                }
                            >
                                {isVerifyingAddPhone ? 'Verifying...' : 'Verify Phone Number'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
